import { useCallback } from 'react';

import { useChainId } from '@occc-app/Chain/hooks';
import { GeneralSurveyFragment } from '@occc/graphql';

import {
  useCreateSurveyOffer,
  useDeleteSurveyOffer,
  useUpdateSurveyOffer,
} from '../../Wallet/hooks';
import { useGeneralSurveyUpdate } from '../../../dataAccessLayer/survey/mutations';
import { GeneralSurveyConfigureFormValues } from '../components/ConfigureForm/types';
import { GeneralSurveyTargetFormValues } from '../components/TargetForm/types';
import { GeneralSurveyScheduleFormValues } from '../components/ScheduleForm/types';
import { convertFormFieldsToUpdateInput } from '../utils/convertFormFieldsToUpdateInput';

type UseUpdateGeneralSurveyParams = {
  onCompleted: (params: { survey: GeneralSurveyFragment; isNew: boolean }) => void;
  onError: (params: { isIncentivizeError: boolean; survey?: GeneralSurveyFragment }) => void;
};

type UpdateGeneralSurveyParams = {
  configureFormValues: GeneralSurveyConfigureFormValues;
  configureFormDirtyFields: Record<string, boolean>;
  targetFormValues: GeneralSurveyTargetFormValues;
  scheduleFormValues: GeneralSurveyScheduleFormValues;
  survey: GeneralSurveyFragment;
  configureFormValuesDirty: boolean;
  targetFormValuesDirty: boolean;
  scheduleFormValuesDirty: boolean;
};

export const useUpdateSurvey = ({ onError, onCompleted }: UseUpdateGeneralSurveyParams) => {
  const chainId = useChainId();
  const [updateGeneralSurvey, { loading: updateSurveyLoading }] = useGeneralSurveyUpdate();
  const { createSurveyOffer, loading: createIncentivizeResponsesLoading } = useCreateSurveyOffer();
  const { updateSurveyOffer, loading: updateIncentivizeResponsesLoading } = useUpdateSurveyOffer();
  const { deleteSurveyOffer, loading: deleteIncentivizeResponsesLoading } = useDeleteSurveyOffer();

  const updateSurvey = useCallback(
    async ({
      configureFormValues,
      configureFormDirtyFields,
      targetFormValues,
      scheduleFormValues,
      survey: oldSurvey,
      configureFormValuesDirty,
      targetFormValuesDirty,
      scheduleFormValuesDirty,
    }: UpdateGeneralSurveyParams) => {
      const input = convertFormFieldsToUpdateInput({
        survey: oldSurvey,
        configure: configureFormValues,
        target: targetFormValues,
        schedule: scheduleFormValues,
        chainId,
      });

      let isSurveyError = false;
      let newSurvey = null;
      if (configureFormValuesDirty || targetFormValuesDirty || scheduleFormValuesDirty) {
        const { data } = await updateGeneralSurvey({ variables: { input } });
        isSurveyError = !data?.generalSurveyUpdate;
        if (data?.generalSurveyUpdate) {
          newSurvey = data.generalSurveyUpdate;
        }
      }
      // TODO: merge incentivize code with Wallet Steering Incentivize code, they are almost the same
      let isIncentivizeError = false;
      const isSurveyAwardExist = !!oldSurvey.award;
      const isIncentivizeChanged =
        configureFormDirtyFields['incentivizeResponses.isEnabled'] ||
        configureFormDirtyFields['incentivizeResponses.awardPoints.points'] ||
        configureFormDirtyFields['incentivizeResponses.awardVouchers.voucheredOffer'];
      const isIncentivizeUpdated =
        isIncentivizeChanged && configureFormValues.incentivizeResponses.isEnabled;
      const isIncentivizeRemoved =
        isIncentivizeChanged && !configureFormValues.incentivizeResponses.isEnabled;

      // If there is an award associated with survey we have to update it
      if (isSurveyAwardExist && isIncentivizeUpdated) {
        const { data } = await updateSurveyOffer({
          surveyUuid: oldSurvey.uuid,
          values: configureFormValues.incentivizeResponses,
        });
        if (!data?.surveyOfferUpdate) {
          isIncentivizeError = true;
        }
      }

      // If there is no an award associated with survey and incentive form values changed we have to create it
      if (!isSurveyAwardExist && isIncentivizeChanged) {
        const { data } = await createSurveyOffer({
          surveyUuid: oldSurvey.uuid,
          values: configureFormValues.incentivizeResponses,
        });
        if (!data?.surveyOfferCreate) {
          isIncentivizeError = true;
        }
      }

      if (isIncentivizeRemoved) {
        const { data } = await deleteSurveyOffer(oldSurvey.uuid);
        if (!data?.surveyOfferDelete) {
          isIncentivizeError = true;
        }
      }

      if (isIncentivizeError) {
        onError({ survey: newSurvey || oldSurvey, isIncentivizeError: true });
        return;
      }
      if (isSurveyError) {
        onError({ survey: newSurvey || oldSurvey, isIncentivizeError: false });
      }

      onCompleted({ survey: newSurvey || oldSurvey, isNew: false });
    },
    [
      chainId,
      createSurveyOffer,
      deleteSurveyOffer,
      onCompleted,
      onError,
      updateGeneralSurvey,
      updateSurveyOffer,
    ],
  );

  return {
    updateSurvey,
    loading:
      updateSurveyLoading ||
      createIncentivizeResponsesLoading ||
      updateIncentivizeResponsesLoading ||
      deleteIncentivizeResponsesLoading,
  };
};
